import { useCallback, useEffect, useState } from 'react';
import { extractParentTheme, isAllowedParentOrigin } from '@/auth/embeddedTokenBridge';
import { applyTheme, type AppTheme } from '@/theme/applyTheme';

const THEME_STORAGE_KEY = 'di-hr-web:theme';

const readStoredTheme = (): AppTheme | null => {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
};

/**
 * Keeps this app's light/dark mode in sync with its host.
 * Embedded (inside the chatbot iframe): the mode is fully driven by
 * `parent:auth-state`/`parent:logout` bridge messages, which already carry
 * the host's current theme (see embeddedTokenBridge.extractParentTheme) --
 * no manual toggle here, the parent owns the theme.
 * Standalone: defaults to the OS color-scheme preference, but a manual
 * choice made via `toggleTheme` is persisted and takes precedence.
 */
export const useEmbeddedTheme = (embeddedMode: boolean) => {
  const [theme, setTheme] = useState<AppTheme>('light');

  useEffect(() => {
    if (!embeddedMode) {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const applyFromSystem = () => {
        const next = readStoredTheme() ?? (media.matches ? 'dark' : 'light');
        applyTheme(next);
        setTheme(next);
      };

      applyFromSystem();
      media.addEventListener('change', applyFromSystem);

      return () => {
        media.removeEventListener('change', applyFromSystem);
      };
    }

    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        return;
      }

      const parentTheme = extractParentTheme(event.data);
      if (parentTheme) {
        applyTheme(parentTheme.theme, parentTheme.colorTheme);
        setTheme(parentTheme.theme);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [embeddedMode]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: AppTheme = prev === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
};
